import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../../../firebase/AuthContext";
import { getApiUrl } from "../../../../../config/api";
import { ComplianceBadge } from "../products/ProductCard";

// ── Mini product card matching ProductCard styling ──────────────────────────────

function MiniProductCard({ product, isSelected, onClick, language, checkbox }) {
    const t = (en, zh) => (language === "zh" ? zh : en);
    return (
        <button
            onClick={onClick}
            className={`w-full text-left group relative bg-white border rounded-xl p-4 transition-all duration-300 ${
                isSelected
                    ? "border-orange-500 shadow-lg shadow-orange-500/10 -translate-y-0.5"
                    : "border-gray-200 hover:border-orange-500/50 hover:shadow-xl hover:shadow-orange-500/10 hover:-translate-y-1"
            }`}
        >
            {/* Hover gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none" />

            {/* Selection indicator */}
            {isSelected && (
                <div className="absolute top-3 right-3 w-6 h-6 bg-orange-500 rounded-xl flex items-center justify-center">
                    <svg
                        className="w-3.5 h-3.5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                        />
                    </svg>
                </div>
            )}

            {/* Checkbox for multi-select */}
            {checkbox && !isSelected && (
                <div className="absolute top-3 right-3 w-6 h-6 border-2 border-gray-300 rounded-xl" />
            )}

            {/* Header */}
            <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                    <h3
                        className={`text-sm font-bold truncate uppercase transition-colors ${
                            isSelected
                                ? "text-orange-500"
                                : "text-gray-900 group-hover:text-orange-500"
                        }`}
                    >
                        {product.model_id}
                    </h3>
                </div>
                {product.description && product.description.trim() !== "" && (
                    <p className="text-xs text-gray-500 truncate mb-2">
                        {product.description}
                    </p>
                )}
            </div>

            {/* Details row */}
            <div className="relative flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <div className="flex items-center gap-1">
                    <svg
                        className="w-3 h-3 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                        />
                    </svg>
                    <span className="text-gray-600 font-mono">
                        #{product.product_id}
                    </span>
                </div>
                {product.producer && (
                    <div className="flex items-center gap-1">
                        <svg
                            className="w-3 h-3 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                        </svg>
                        <span className="text-gray-700 font-medium capitalize">
                            {product.producer}
                        </span>
                    </div>
                )}
                <div className="flex items-center gap-1">
                    <svg
                        className="w-3 h-3 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <span className="text-gray-500">
                        {t("Ver", "版本")}:{" "}
                        <span className="font-medium text-gray-700">
                            {product.current_version_label || "—"}
                        </span>
                    </span>
                </div>
            </div>

            {/* Compliance badges */}
            {(product.rohs_compliant ||
                product.reach_compliant ||
                product.ul_comparable) && (
                <div className="relative flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-100">
                    {product.rohs_compliant && (
                        <ComplianceBadge
                            label="RoHS"
                            isCompliant={product.rohs_compliant}
                        />
                    )}
                    {product.reach_compliant && (
                        <ComplianceBadge
                            label="REACH"
                            isCompliant={product.reach_compliant}
                        />
                    )}
                    {product.ul_comparable && (
                        <ComplianceBadge
                            label="UL"
                            isCompliant={product.ul_comparable}
                        />
                    )}
                </div>
            )}
        </button>
    );
}

// ── Product-picker panel (single-select) ────────────────────────────────────────

function ProductPickerPanel({
    title,
    products,
    selected,
    onSelect,
    excludeIds = [],
    language,
}) {
    const [search, setSearch] = useState("");
    const t = (en, zh) => (language === "zh" ? zh : en);

    const filtered = products.filter((p) => {
        if (excludeIds.includes(p.product_id)) return false;
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (
            p.model_id?.toLowerCase().includes(q) ||
            String(p.product_id).includes(q) ||
            p.description?.toLowerCase().includes(q)
        );
    });

    return (
        <div className="flex flex-col h-full border border-gray-200 rounded-xl overflow-hidden bg-white">
            {/* Panel header */}
            <div className="px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {title}
                    </p>
                    <span className="text-xs text-gray-400">
                        {filtered.length} {t("products", "個產品")}
                    </span>
                </div>
                <div className="relative">
                    <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("Search products…", "搜尋產品…")}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all"
                    />
                </div>
            </div>

            {/* Product list */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50/50">
                {filtered.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                        <svg
                            className="w-8 h-8 mb-2 text-gray-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                            />
                        </svg>
                        <span className="text-sm">
                            {t(
                                "No products match your search.",
                                "沒有符合的產品。",
                            )}
                        </span>
                    </div>
                )}
                {filtered.map((p) => (
                    <MiniProductCard
                        key={p.product_id}
                        product={p}
                        isSelected={selected?.product_id === p.product_id}
                        onClick={() =>
                            onSelect(
                                selected?.product_id === p.product_id
                                    ? null
                                    : p,
                            )
                        }
                        language={language}
                    />
                ))}
            </div>

            {/* Selected footer */}
            <div
                className={`px-4 py-2.5 border-t flex-shrink-0 text-xs font-medium transition-colors ${
                    selected
                        ? "bg-orange-50 border-orange-200 text-orange-700"
                        : "bg-white border-gray-200 text-gray-400"
                }`}
            >
                {selected
                    ? `✓ ${selected.model_id} (#${selected.product_id}) ${t("selected", "已選擇")}`
                    : t("No product selected", "尚未選擇產品")}
            </div>
        </div>
    );
}

// ── Multi-select product picker panel ───────────────────────────────────────────

function MultiProductPickerPanel({
    title,
    products,
    selectedList = [],
    onToggle,
    excludeIds = [],
    language,
}) {
    const [search, setSearch] = useState("");
    const t = (en, zh) => (language === "zh" ? zh : en);

    const selectedIds = new Set(selectedList.map((p) => p.product_id));

    const filtered = products.filter((p) => {
        if (excludeIds.includes(p.product_id)) return false;
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (
            p.model_id?.toLowerCase().includes(q) ||
            String(p.product_id).includes(q) ||
            p.description?.toLowerCase().includes(q)
        );
    });

    return (
        <div className="flex flex-col h-full border border-gray-200 rounded-xl overflow-hidden bg-white">
            {/* Panel header */}
            <div className="px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {title}
                    </p>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">
                            {filtered.length} {t("products", "個產品")}
                        </span>
                        {selectedList.length > 0 && (
                            <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-xl font-semibold">
                                {selectedList.length} {t("selected", "已選")}
                            </span>
                        )}
                    </div>
                </div>
                <div className="relative">
                    <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("Search products…", "搜尋產品…")}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all"
                    />
                </div>
            </div>

            {/* Product list */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50/50">
                {filtered.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                        <svg
                            className="w-8 h-8 mb-2 text-gray-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                            />
                        </svg>
                        <span className="text-sm">
                            {t(
                                "No products match your search.",
                                "沒有符合的產品。",
                            )}
                        </span>
                    </div>
                )}
                {filtered.map((p) => (
                    <MiniProductCard
                        key={p.product_id}
                        product={p}
                        isSelected={selectedIds.has(p.product_id)}
                        onClick={() => onToggle(p)}
                        language={language}
                        checkbox
                    />
                ))}
            </div>

            {/* Selected footer */}
            <div
                className={`px-4 py-2.5 border-t flex-shrink-0 text-xs font-medium transition-colors ${
                    selectedList.length > 0
                        ? "bg-orange-50 border-orange-200 text-orange-700"
                        : "bg-white border-gray-200 text-gray-400"
                }`}
            >
                {selectedList.length > 0
                    ? `✓ ${selectedList.length} ${t("product(s) selected", "個產品已選擇")}`
                    : t("No products selected", "尚未選擇產品")}
            </div>
        </div>
    );
}

// ── Main panel ──────────────────────────────────────────────────────────────────

export default function ProductDuplicatesPanel({ language, onMergeComplete }) {
    const { currentUser } = useAuth();

    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    const [leftProduct, setLeftProduct] = useState(null); // canonical (keep)
    const [rightProducts, setRightProducts] = useState([]); // duplicates (merge away)

    const [merging, setMerging] = useState(false);
    const [mergeResult, setMergeResult] = useState(null);
    const [mergeError, setMergeError] = useState("");

    const t = (en, zh) => (language === "zh" ? zh : en);

    // Fetch the full product catalog for this panel — it needs every product
    // to detect duplicates, not just one paginated page from the database tab.
    const fetchAllProducts = useCallback(async () => {
        setLoadingProducts(true);
        try {
            const token = await currentUser.getIdToken();
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.get(
                `${getApiUrl()}/api/v2/products/all`,
                config,
            );
            setProducts(data.products || []);
        } catch (err) {
            console.error("Error fetching products for duplicates panel:", err);
            setProducts([]);
        } finally {
            setLoadingProducts(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchAllProducts();
    }, [fetchAllProducts]);

    // Toggle a product in/out of the right (duplicates) list
    const handleToggleRight = (product) => {
        setRightProducts((prev) => {
            const exists = prev.find(
                (p) => p.product_id === product.product_id,
            );
            if (exists)
                return prev.filter((p) => p.product_id !== product.product_id);
            return [...prev, product];
        });
        setMergeResult(null);
        setMergeError("");
    };

    // ── merge ─────────────────────────────────────────────────────────────

    const handleMerge = async () => {
        if (!leftProduct || rightProducts.length === 0) return;
        setMerging(true);
        setMergeError("");
        setMergeResult(null);

        try {
            const token = await currentUser.getIdToken();
            const allIds = [
                leftProduct.product_id,
                ...rightProducts.map((p) => p.product_id),
            ];
            const res = await fetch(`${getApiUrl()}/api/v2/products/merge`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ product_ids: allIds }),
            });
            const json = await res.json();
            if (!json.success) throw new Error(json.error || "Merge failed");
            setMergeResult(json.data);
            setLeftProduct(null);
            setRightProducts([]);
            fetchAllProducts();
            onMergeComplete?.();
        } catch (err) {
            setMergeError(err.message);
        } finally {
            setMerging(false);
        }
    };

    // ── helpers ────────────────────────────────────────────────────────────

    const rightIds = rightProducts.map((p) => p.product_id);
    const bothSelected = leftProduct && rightProducts.length > 0;

    // Figure out canonical among all selected products (lowest ID)
    const allSelected = bothSelected ? [leftProduct, ...rightProducts] : [];
    const canonical = bothSelected
        ? allSelected.reduce((a, b) => (a.product_id < b.product_id ? a : b))
        : null;
    const duplicates = bothSelected
        ? allSelected
              .filter((p) => p.product_id !== canonical.product_id)
              .sort((a, b) => a.product_id - b.product_id)
        : [];

    return (
        <div className="space-y-5">
            {/* Page header — matching DatabaseView header card */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-xl font-bold text-gray-900 mb-1">
                            {t("Merge Duplicate Products", "合併重複產品")}
                        </h2>
                        <p className="text-gray-600 text-sm">
                            {t(
                                "Select products to merge. Pick one on the left, then one or more duplicates on the right. The oldest (lowest ID) will automatically be kept.",
                                "選擇要合併的產品。在左側選擇一個產品，然後在右側選擇一個或多個重複項。ID 最小（最舊）的產品將自動保留。",
                            )}
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            fetchAllProducts();
                            setLeftProduct(null);
                            setRightProducts([]);
                            setMergeResult(null);
                            setMergeError("");
                        }}
                        disabled={loadingProducts}
                        className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all text-sm font-medium flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg
                            className={`w-4 h-4 ${loadingProducts ? "animate-spin" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                        </svg>
                        {t("Refresh", "重新整理")}
                    </button>
                </div>
            </div>

            {/* Success banner */}
            {mergeResult && (
                <div className="flex items-start gap-3 px-5 py-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 shadow-sm">
                    <div className="w-8 h-8 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </div>
                    <div>
                        <p className="font-semibold">
                            {t("Merge successful!", "合併成功！")}
                        </p>
                        <p className="text-green-600 mt-0.5">
                            {t(
                                `${mergeResult.duplicates_merged} duplicate(s) merged — ${mergeResult.total_versions_moved} version(s) and ${mergeResult.total_channel_pins_moved} channel pin(s) moved into product #${mergeResult.canonical_product_id}.`,
                                `已合併 ${mergeResult.duplicates_merged} 個重複項 — ${mergeResult.total_versions_moved} 個版本和 ${mergeResult.total_channel_pins_moved} 個頻道綁定已移至產品 #${mergeResult.canonical_product_id}。`,
                            )}
                        </p>
                    </div>
                </div>
            )}

            {/* Error banner */}
            {mergeError && (
                <div className="flex items-center gap-3 px-5 py-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 shadow-sm">
                    <div className="w-8 h-8 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    </div>
                    <p className="font-medium">{mergeError}</p>
                </div>
            )}

            {/* Dual picker */}
            {loadingProducts ? (
                <div
                    className="flex items-center justify-center bg-white border border-gray-200 rounded-xl shadow-sm"
                    style={{ height: "420px" }}
                >
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <svg
                            className="w-4 h-4 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v8z"
                            />
                        </svg>
                        {t("Loading products…", "正在載入產品…")}
                    </div>
                </div>
            ) : (
            <div className="grid grid-cols-2 gap-4" style={{ height: "420px" }}>
                <ProductPickerPanel
                    title={t("Canonical Product", "主要產品")}
                    products={products}
                    selected={leftProduct}
                    onSelect={(p) => {
                        setLeftProduct(p);
                        setMergeResult(null);
                        setMergeError("");
                    }}
                    excludeIds={rightIds}
                    language={language}
                />
                <MultiProductPickerPanel
                    title={t("Duplicates to Merge", "要合併的重複產品")}
                    products={products}
                    selectedList={rightProducts}
                    onToggle={handleToggleRight}
                    excludeIds={leftProduct ? [leftProduct.product_id] : []}
                    language={language}
                />
            </div>
            )}

            {/* Comparison + merge action */}
            {bothSelected && canonical && (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    {/* Auto-canonical notice */}
                    <div className="flex items-center gap-2 px-5 py-3 bg-orange-50 border-b border-orange-100 text-sm text-orange-700">
                        <svg
                            className="w-4 h-4 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                            />
                        </svg>
                        {t(
                            `Product #${canonical.product_id} (oldest) will be kept. ${duplicates.length} duplicate(s) will be merged into it and deleted.`,
                            `產品 #${canonical.product_id}（最舊）將被保留。${duplicates.length} 個重複項將合併入其中並刪除。`,
                        )}
                    </div>

                    {/* Summary — card-based */}
                    <div className="p-5 space-y-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            {t("Merge Summary", "合併摘要")}
                        </p>

                        {/* Canonical product — card style */}
                        <div className="group relative bg-white border-2 border-orange-300 rounded-xl p-4 shadow-sm">
                            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent rounded-xl pointer-events-none" />
                            <div className="relative flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-xl font-semibold shadow-sm">
                                            {t("KEEP", "保留")}
                                        </span>
                                        <h3 className="text-base font-bold text-orange-600 truncate uppercase">
                                            {canonical.model_id}
                                        </h3>
                                    </div>
                                    {canonical.description &&
                                        canonical.description.trim() !== "" && (
                                            <p className="text-xs text-gray-500 truncate mb-2">
                                                {canonical.description}
                                            </p>
                                        )}
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                                        <div className="flex items-center gap-1">
                                            <svg
                                                className="w-3.5 h-3.5 text-gray-400"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                                />
                                            </svg>
                                            <span className="text-gray-600 font-mono">
                                                #{canonical.product_id}
                                            </span>
                                        </div>
                                        {canonical.producer && (
                                            <div className="flex items-center gap-1">
                                                <svg
                                                    className="w-3.5 h-3.5 text-gray-400"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                                    />
                                                </svg>
                                                <span className="text-gray-700 font-medium capitalize">
                                                    {canonical.producer}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1">
                                            <svg
                                                className="w-3.5 h-3.5 text-gray-400"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            <span className="text-gray-500">
                                                {t("Ver", "版本")}:{" "}
                                                <span className="font-medium text-gray-700">
                                                    {canonical.current_version_label ||
                                                        "—"}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                {(canonical.rohs_compliant ||
                                    canonical.reach_compliant ||
                                    canonical.ul_comparable) && (
                                    <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
                                        {canonical.rohs_compliant && (
                                            <ComplianceBadge
                                                label="RoHS"
                                                isCompliant={
                                                    canonical.rohs_compliant
                                                }
                                            />
                                        )}
                                        {canonical.reach_compliant && (
                                            <ComplianceBadge
                                                label="REACH"
                                                isCompliant={
                                                    canonical.reach_compliant
                                                }
                                            />
                                        )}
                                        {canonical.ul_comparable && (
                                            <ComplianceBadge
                                                label="UL"
                                                isCompliant={
                                                    canonical.ul_comparable
                                                }
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Arrow indicator */}
                        <div className="flex justify-center">
                            <div className="w-8 h-8 bg-gray-100 rounded-xl flex items-center justify-center">
                                <svg
                                    className="w-4 h-4 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                                    />
                                </svg>
                            </div>
                        </div>

                        {/* Duplicate products — card style */}
                        <div className="space-y-2">
                            {duplicates.map((dup) => (
                                <div
                                    key={dup.product_id}
                                    className="group relative bg-white border border-gray-200 rounded-xl p-4 hover:border-red-300 transition-all duration-300"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xs bg-gray-500 text-white px-2 py-0.5 rounded-xl font-semibold">
                                                    {t("MERGE", "合併")}
                                                </span>
                                                <h3 className="text-base font-bold text-gray-700 truncate uppercase">
                                                    {dup.model_id}
                                                </h3>
                                            </div>
                                            {dup.description &&
                                                dup.description.trim() !==
                                                    "" && (
                                                    <p className="text-xs text-gray-500 truncate mb-2">
                                                        {dup.description}
                                                    </p>
                                                )}
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                                                <div className="flex items-center gap-1">
                                                    <svg
                                                        className="w-3.5 h-3.5 text-gray-400"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                                        />
                                                    </svg>
                                                    <span className="text-gray-600 font-mono">
                                                        #{dup.product_id}
                                                    </span>
                                                </div>
                                                {dup.producer && (
                                                    <div className="flex items-center gap-1">
                                                        <svg
                                                            className="w-3.5 h-3.5 text-gray-400"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                                            />
                                                        </svg>
                                                        <span className="text-gray-700 font-medium capitalize">
                                                            {dup.producer}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-1">
                                                    <svg
                                                        className="w-3.5 h-3.5 text-gray-400"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                    <span className="text-gray-500">
                                                        {t("Ver", "版本")}:{" "}
                                                        <span className="font-medium text-gray-700">
                                                            {dup.current_version_label ||
                                                                "—"}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        {(dup.rohs_compliant ||
                                            dup.reach_compliant ||
                                            dup.ul_comparable) && (
                                            <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
                                                {dup.rohs_compliant && (
                                                    <ComplianceBadge
                                                        label="RoHS"
                                                        isCompliant={
                                                            dup.rohs_compliant
                                                        }
                                                    />
                                                )}
                                                {dup.reach_compliant && (
                                                    <ComplianceBadge
                                                        label="REACH"
                                                        isCompliant={
                                                            dup.reach_compliant
                                                        }
                                                    />
                                                )}
                                                {dup.ul_comparable && (
                                                    <ComplianceBadge
                                                        label="UL"
                                                        isCompliant={
                                                            dup.ul_comparable
                                                        }
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Merge action */}
                    <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-t border-gray-200">
                        <p className="text-xs text-gray-500 max-w-sm">
                            {t(
                                "All versions from the duplicate products will be moved to the canonical product. The duplicate rows will then be deleted.",
                                "重複產品的所有版本將移至主要產品，重複的產品列將被刪除。",
                            )}
                        </p>
                        <button
                            onClick={handleMerge}
                            disabled={merging}
                            className={`ml-4 flex-shrink-0 flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                                merging
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50"
                            }`}
                        >
                            {merging ? (
                                <>
                                    <svg
                                        className="w-4 h-4 animate-spin"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v8z"
                                        />
                                    </svg>
                                    {t("Merging…", "合併中…")}
                                </>
                            ) : (
                                <>
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                                        />
                                    </svg>
                                    {t(
                                        `Merge ${duplicates.length} product(s)`,
                                        `合併 ${duplicates.length} 個產品`,
                                    )}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Placeholder when not both selected yet */}
            {!bothSelected && (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm text-center">
                    <div className="w-16 h-16 bg-orange-50 rounded-xl flex items-center justify-center mb-4">
                        <svg
                            className="w-8 h-8 text-orange-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                        </svg>
                    </div>
                    <p className="text-gray-900 text-base font-medium mb-1">
                        {!leftProduct && rightProducts.length === 0
                            ? t("Select products to merge", "選擇要合併的產品")
                            : !leftProduct
                              ? t(
                                    "Select a product on the left",
                                    "請在左側選擇一個產品",
                                )
                              : t(
                                    "Select duplicates on the right",
                                    "請在右側選擇重複產品",
                                )}
                    </p>
                    <p className="text-gray-500 text-sm max-w-xs">
                        {!leftProduct && rightProducts.length === 0
                            ? t(
                                  "Pick one product on the left panel, then select one or more duplicates on the right panel to begin.",
                                  "在左側面板選擇一個產品，然後在右側面板選擇一個或多個重複項以開始。",
                              )
                            : !leftProduct
                              ? t(
                                    "Choose the product you want to keep from the left panel.",
                                    "從左側面板選擇您要保留的產品。",
                                )
                              : t(
                                    "Select the duplicate products to merge from the right panel.",
                                    "從右側面板選擇要合併的重複產品。",
                                )}
                    </p>
                </div>
            )}
        </div>
    );
}
